// Development Environment Guards - Prevent common development issues

export class DevelopmentGuards {
  private static hasWarned = new Set<string>();

  static checkEnvironment(): { valid: boolean; issues: string[] } {
    const issues: string[] = [];

    // Check if we're in development
    if (process.env.NODE_ENV !== 'development') {
      return { valid: true, issues: [] };
    }

    // Check API URL configuration
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) {
      issues.push('REACT_APP_API_URL not set - will use default localhost:3001');
    } else if (!apiUrl.startsWith('http')) {
      issues.push(`Invalid REACT_APP_API_URL: ${apiUrl} (must start with http/https)`);
    }

    // Check for common port conflicts
    this.checkPortAvailability();

    // Warn about persistence service usage
    this.warnAboutApiCalls();

    return { valid: issues.length === 0, issues };
  }

  private static async checkPortAvailability() {
    // Only check backend port 3001 for health endpoint
    const backendPort = 3001;
    
    try {
      const response = await fetch(`http://localhost:${backendPort}/health`, {
        signal: AbortSignal.timeout(1000)
      });
      
      if (response.ok) {
        this.logOnce(`port-${backendPort}-available`, `✅ Port ${backendPort} is available`);
      }
    } catch {
      this.logOnce(`port-${backendPort}-unavailable`, `⚠️ Port ${backendPort} may not be available`);
    }
  }

  private static warnAboutApiCalls() {
    // Override fetch to warn about API calls without proper checks
    if (typeof window !== 'undefined' && !window.fetch['__development_guarded']) {
      const originalFetch = window.fetch;
      
      window.fetch = async (...args) => {
        const url = args[0]?.toString() || '';
        
        // Check for potentially problematic API calls
        if (url.includes('/api/') && !url.includes('/health')) {
          const hasToken = localStorage.getItem('token');
          
          if (!hasToken) {
            this.logOnce(
              `api-call-warning-${url}`,
              `🚨 API call without auth token: ${url}\n` +
              `Consider using ApiGateway.canMakeApiCall() first`
            );
          }
        }
        
        return originalFetch(...args);
      };
      
      window.fetch['__development_guarded'] = true;
    }
  }

  private static logOnce(key: string, message: string) {
    if (!this.hasWarned.has(key)) {
      console.warn(`[Dev Guards] ${message}`);
      this.hasWarned.add(key);
    }
  }

  // Startup validation - call this in main App component
  static async validateStartup(): Promise<void> {
    if (process.env.NODE_ENV !== 'development') return;

    console.log('🛡️ Running development environment validation...');
    
    const { valid, issues } = this.checkEnvironment();
    
    if (!valid) {
      console.warn('⚠️ Development environment issues detected:');
      issues.forEach(issue => console.warn(`  • ${issue}`));
    }

    // Ensure password protection is bypassed in development
    this.ensurePasswordProtectionBypass();

    // Check if persistence service will work
    await this.validatePersistenceService();
    
    console.log('✅ Development validation complete');
  }

  private static ensurePasswordProtectionBypass() {
    // Always set in development mode, regardless of current value
    localStorage.setItem('thinkmate_access_granted', 'true');
    localStorage.setItem('thinkmate_access_granted_at', Date.now().toString());
    
    this.logOnce(
      'password-protection-bypass',
      '🔓 Development mode: Password protection bypassed - localStorage flags set'
    );

    // Debug log to verify
    console.log('🔧 Dev Guards: Access granted flag set to:', localStorage.getItem('thinkmate_access_granted'));
  }


  private static async validatePersistenceService() {
    const hasToken = localStorage.getItem('token');
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

    if (!hasToken) {
      this.logOnce(
        'persistence-no-token',
        '📝 Persistence service will use localStorage (no auth token)'
      );
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/health`, {
        signal: AbortSignal.timeout(2000)
      });
      
      if (response.ok) {
        this.logOnce(
          'persistence-server-available',
          '📝 Persistence service will use server + localStorage'
        );
      } else {
        this.logOnce(
          'persistence-server-error',
          '📝 Persistence service will use localStorage (server error)'
        );
      }
    } catch {
      this.logOnce(
        'persistence-server-unavailable',
        '📝 Persistence service will use localStorage (server unavailable)'
      );
    }
  }
}

export default DevelopmentGuards;